import { Button, Table, TableBody, TableCell, TableHead, TableRow, Tooltip } from '@mui/material';
import _ from 'lodash';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Utility from '../../app/utility';
import FixtureModel from '../FixtureManagement/models/FixtureModel';
import { setSelectedFixtureId, setShowInputPanel } from '../FixtureManagement/reducers/FixtureInputReducer';
import { userProfileSelector } from '../Login/reducers/UserProfileReducer';
import SupportSheetInvoiceModel from '../SupportSheet/components/SupportSheetInvoice/models/SupportSheetInvoiceModel';
import { SupportSheetStatus } from '../SupportSheet/models/SupportSheetModel';
import { UserGroupEnum } from '../UserManagement/UserGroupEnum';
import { MyFixtureSelector, setOrderColumn, setOrderDirection } from './reducers/MyFixtureReducer';
import FixtureApi, { DeleteParam, SplitFixtureParam } from '../FixtureManagement/api/FixtureApi';
import BaseResponse from '../../app/BaseResponse';
import { showLoading } from '../LoadingView/reducers/LoadingViewReducer';
import Swal from 'sweetalert2';
import SupportSheetAnalysisModel from '../SupportSheet/components/SupportSheetAnalysis/models/SupportSheetAnalysisModel';
import CommissionItemModel from '../FixtureManagement/components/FixtureCommissionDetail/models/CommissionItemModel';
import { isInRole } from '../MenuBar/MenuBar';
import { OrderDirection } from '../Datatable/OrderDirection';

const MyFixtureTable: React.FunctionComponent = () => {
    let dispatch = useDispatch();
    let myFixtureData = useSelector(MyFixtureSelector);
    const userProfile = useSelector(userProfileSelector);

    let renderMyFixtures = () => {
        let fixtures = sortMyFixtures();

        return fixtures.map((item: FixtureModel, index: number) => {
            return <TableRow>
                {renderFixtureRow(item, index)}
            </TableRow>;
        });
    }

    let sortMyFixtures = () => {
        let myFixtures = _.sortBy(myFixtureData.fixtures, (fixture) => {
            if (myFixtureData.orderColumn == "cpDate") {
                return new Date(fixture.cpDate);
            } else {
                return fixture[myFixtureData.orderColumn];
            }
        });

        return myFixtureData.orderDirection == OrderDirection.Asc ? myFixtures : myFixtures.reverse();
    }

    let renderFixtureRow = (item: FixtureModel, index: number) => {
        let analysis = JSON.parse(item.supportSheetModel.analysis) as SupportSheetAnalysisModel;

        let commDetails = JSON.parse(item.commRateDetail) as CommissionItemModel[];

        let commRateDetail = "";
        _.each(commDetails, (commItem) => {
            commRateDetail += commItem.brokerName + commItem.percent + " ";
        })

        return <>
            <TableCell>{index+1}</TableCell>
            <TableCell>{item.status}</TableCell>
            <TableCell>{item.ref}</TableCell>
            <TableCell>{item.tonnageVesselName}</TableCell>
            <TableCell>{Utility.DateFormat.formatDateString(item.cpDate)}</TableCell>
            <TableCell>
                {commRateDetail}
            </TableCell>
            <TableCell>{item.supportName}</TableCell>
            <TableCell>{item.invoiceNo}</TableCell>
            <TableCell>{item.issDate && item.issDate.length > 0 ? Utility.DateFormat.formatDateString(item.issDate) : ""}</TableCell>
            <TableCell>{item.amount == 0 ? "" : Utility.NumberFormat.formatUSD(item.amount ?? 0)}</TableCell>
            <TableCell>{item.bankReceived - item.privateCommSettled == 0 ? "" : Utility.NumberFormat.formatUSD(item.bankReceived - item.privateCommSettled)}</TableCell>
            <TableCell>{item.bankReceived == 0 ? "" : Utility.NumberFormat.formatUSD(item.bankReceived)}</TableCell>
            <TableCell>{item.rcvDate && item.rcvDate.length > 0 ? Utility.DateFormat.formatDateString(item.rcvDate) : ""}</TableCell>
            <TableCell>{analysis ? analysis.postfixPICVolume : ""}</TableCell>
            <TableCell>{analysis ? analysis.postfixPICComplexity : ""}</TableCell>
            <TableCell>{analysis ? analysis.postfixPICQuality : ""}</TableCell>
            <TableCell>{analysis ? analysis.postfixManagerQuality : ""}</TableCell>
            <TableCell>{analysis ? analysis.brokerPICQuality : ""}</TableCell>
            <TableCell className='display-linebreak'>{!item.supportSheetNote || item.supportSheetNote.length <= 50 ? item.supportSheetNote : item.supportSheetNote.substring(0, 15)}</TableCell>
            <TableCell>
                <Tooltip title="Edit">
                    <Button
                        className='table-action-button'
                        onClick={() => {
                            dispatch(setSelectedFixtureId(item.id));
                            dispatch(setShowInputPanel(true));
                        }}
                        variant='outlined'
                    >
                        <i className="fa fa-pencil" aria-hidden="true"></i>
                    </Button>
                </Tooltip>
                <Tooltip title="View Support Sheet">
                    <Button
                        className='table-action-button'
                        onClick={() => {
                            let url = "/support-sheet?fixtureId=" + item.id;

                            const newWindow = window.open(url, '_blank', 'noopener,noreferrer')
                            if (newWindow) newWindow.opener = null
                        }}
                        variant='outlined'
                    >
                        <i className="fa fa-file-text-o" aria-hidden="true"></i>
                    </Button>
                </Tooltip>
                <Tooltip title="Split Fixture">
                    <Button
                        className='table-action-button'
                        onClick={() => {
                            FixtureApi.SplitFixture({
                                id: item.id
                            } as SplitFixtureParam)
                                .then((response: BaseResponse<string>) => {
                                    if (response.success) {
                                        Utility.Alert.Success("Split fixture successful");
                                    } else {
                                        dispatch(showLoading(false));
                                        Utility.Alert.Error(response.message);
                                    }
                                })
                                .catch((error) => {
                                    dispatch(showLoading(false));
                                    Utility.Alert.Error(error);
                                });
                        }}
                        variant='outlined'
                    >
                        <i className="fa fa-columns" aria-hidden="true"></i>
                    </Button>
                </Tooltip>
                {isInRole(userProfile.userGroup, [UserGroupEnum.Administrator, UserGroupEnum.SupportManager]) &&
                    <Tooltip title="Delete Fixture">
                        <Button
                            className='table-action-button'
                            variant='outlined'
                            color='error'
                            onClick={() => {
                                Swal.fire({
                                    title: 'Are you sure want to delete this Fixture? This action can not be undone.',
                                    icon: 'warning',
                                    showCancelButton: true,
                                    confirmButtonColor: '#3085d6',
                                    cancelButtonColor: '#d33',
                                    confirmButtonText: 'Yes',
                                    cancelButtonText: 'No'
                                }).then((result) => {
                                    if (result.isConfirmed) {
                                        dispatch(showLoading(true));
                                        FixtureApi.Delete({
                                            id: item.id
                                        } as DeleteParam)
                                            .then((response: BaseResponse<FixtureModel[]>) => {
                                                if (response.success) {
                                                    Utility.Alert.Success("Fixture deleted successful");

                                                    dispatch(showLoading(false));
                                                }
                                            });
                                    }
                                })
                            }}
                        >
                            <i className="fa fa-trash" aria-hidden="true"></i>
                        </Button>
                    </Tooltip>
                }
            </TableCell>
        </>
    }

    let renderHeader = (className: string, rowSpan: number, colSpan: number, headerName: string, allowHeaderSort: boolean, fieldName: keyof FixtureModel = "ref") => {
        let headerSortClass = myFixtureData.orderColumn === fieldName ?
            "datatable-header-sort-wrapper-active" :
            "datatable-header-sort-wrapper";

        let tableCellHeader = allowHeaderSort ?
            "datatable-cell-header datatable-cell-sortable" :
            "datatable-cell-header";

        if (className && className.length > 0) {
            tableCellHeader += " " + className;
        }

        return <TableCell className={tableCellHeader} rowSpan={rowSpan} colSpan={colSpan}
            onClick={() => {
                if (allowHeaderSort) {
                    let sortDirection = fieldName !== myFixtureData.orderColumn ?
                        myFixtureData.orderDirection === undefined ? OrderDirection.Asc : myFixtureData.orderDirection :
                        myFixtureData.orderDirection === OrderDirection.Asc ? OrderDirection.Desc : OrderDirection.Asc;

                    dispatch(setOrderColumn(fieldName));
                    dispatch(setOrderDirection(sortDirection));
                }
            }}
        >
            {headerName}
            {allowHeaderSort &&
                <span className={headerSortClass}>
                    {
                        myFixtureData.orderDirection === OrderDirection.Desc &&
                        <Button
                            className={"datatable-header-sort-button"}
                        >
                            <i className="fa fa-sort-desc" aria-hidden="true"></i>
                        </Button>
                    }
                    {
                        myFixtureData.orderDirection !== OrderDirection.Desc &&
                        <Button
                            className={"datatable-header-sort-button"}
                        >
                            <i className="fa fa-sort-asc" aria-hidden="true"></i>
                        </Button>
                    }
                </span>
            }
        </TableCell>;
    }

    return <div className='datatable-wrapper'>
        <div className='my-fixture-table nice-scroll fixture-same-line'>
            <Table className=''>
                <TableHead>
                    <TableRow className="datatable-head">
                        {renderHeader("sticky-first-header-row myfixture-col-sticky myfixture-col-header myfixture-col1", 2, 1, "NO", false, "status")}
                        {renderHeader("sticky-first-header-row myfixture-col-sticky myfixture-col-header myfixture-col1", 2, 1, "STATUS", true, "status")}
                        {renderHeader("sticky-first-header-row myfixture-col-sticky myfixture-col-header myfixture-col2", 2, 1, "REF NO", true, "ref")}
                        {renderHeader("sticky-first-header-row myfixture-col-sticky myfixture-col-header myfixture-col3", 2, 1, "VESSEL'S NAME", true, "tonnageVesselName")}
                        {renderHeader("sticky-first-header-row myfixture-col-sticky myfixture-col-header myfixture-col4", 2, 1, "CP DATED", true, "cpDate")}
                        {renderHeader("sticky-first-header-row myfixture-col-sticky myfixture-col-header myfixture-col5", 2, 1, "BROKERS", false)}
                        {renderHeader("sticky-first-header-row myfixture-col-sticky myfixture-col-header myfixture-col6", 2, 1, "SUPPORT", true, "supportName")}
                        {renderHeader("my-fixture-commission sticky-first-header-row", 1, 6, "COMMISSION", false)}
                        {renderHeader("my-fixture-commission sticky-first-header-row", 1, 5, "EVALUATION", false)}
                        {renderHeader("sticky-first-header-row", 2, 1, "NOTE", false)}
                        {renderHeader("sticky-first-header-row", 2, 1, "", false)}
                    </TableRow>
                    <TableRow className="datatable-head">
                        {renderHeader("sticky-second-header-row", 1, 1, "INV NO", true, "invoiceNo")}
                        {renderHeader("sticky-second-header-row", 1, 1, "ISS DATE", true, "issDate")}
                        {renderHeader("sticky-second-header-row", 1, 1, "AMOUNT", true, "amount")}
                        {renderHeader("sticky-second-header-row", 1, 1, "CALCULATED", true, "commCalculated")}
                        {renderHeader("sticky-second-header-row", 1, 1, "BANK RCV", true, "bankReceived")}
                        {renderHeader("sticky-second-header-row", 1, 1, "RECEIVED", true, "rcvDate")}
                        {renderHeader("sticky-second-header-row", 1, 1, "V", true, "evaVolume")}
                        {renderHeader("sticky-second-header-row", 1, 1, "C", true, "evaComplex")}
                        {renderHeader("sticky-second-header-row", 1, 1, "S", true, "evaSupport")}
                        {renderHeader("sticky-second-header-row", 1, 1, "M", true, "evaManager")}
                        {renderHeader("sticky-second-header-row", 1, 1, "B", true, "evaBroker")}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {renderMyFixtures()}
                </TableBody>
            </Table>
        </div>
    </div>
};

export default MyFixtureTable;